import { useState, useEffect } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import {
  FileText, Download, Calendar, Mail, Clock, CheckCircle, XCircle,
  TrendingUp, TrendingDown, BarChart3, PieChart, RefreshCw, Plus,
  Send, Trash2, Settings, AlertTriangle, Shield, Loader2, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend, PieChart as RechartsPie, Pie, Cell
} from 'recharts';

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function ComplianceReporting() {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('reports');
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [trendData, setTrendData] = useState([]);
  
  // New schedule form
  const [newSchedule, setNewSchedule] = useState({
    name: '',
    frequency: 'weekly',
    report_type: 'summary',
    recipients: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reportsRes, schedulesRes] = await Promise.all([
        api.get('/compliance/reports'),
        api.get('/compliance/reports/schedules')
      ]);
      setReports(reportsRes.data.reports || []);
      setSchedules(schedulesRes.data.schedules || []);
      
      // Generate mock trend data
      const trends = [];
      for (let i = 30; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        trends.push({
          date: date.toISOString().split('T')[0],
          compliance: 65 + Math.random() * 20,
          owasp: 70 + Math.random() * 15,
          pci: 60 + Math.random() * 25,
          gdpr: 75 + Math.random() * 10
        });
      }
      setTrendData(trends);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async (reportType = 'summary') => {
    setGenerating(true);
    try {
      const response = await api.post('/compliance/reports/generate', {
        report_type: reportType,
        standards: [],
        date_range: 30
      });
      toast.success('Report generated successfully');
      setSelectedReport(response.data);
      setShowReportDialog(true);
      fetchData();
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      await api.post('/compliance/digest/send', {});
      toast.success('Compliance digest sent');
    } catch (error) {
      toast.error('Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const createSchedule = async () => {
    try {
      await api.post('/compliance/reports/schedule', {
        ...newSchedule,
        recipients: newSchedule.recipients.split(',').map(r => r.trim()).filter(r => r)
      });
      toast.success('Schedule created');
      setShowScheduleDialog(false);
      setNewSchedule({ name: '', frequency: 'weekly', report_type: 'summary', recipients: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create schedule');
    }
  };

  const deleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/compliance/reports/schedules/${scheduleId}`);
      toast.success('Schedule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const getComplianceColor = (rate) => {
    if (rate >= 80) return 'text-green-500';
    if (rate >= 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getComplianceBg = (rate) => {
    if (rate >= 80) return isDarkMode ? 'bg-green-500/20' : 'bg-green-100';
    if (rate >= 50) return isDarkMode ? 'bg-yellow-500/20' : 'bg-yellow-100';
    return isDarkMode ? 'bg-red-500/20' : 'bg-red-100';
  };

  // Calculate summary stats
  const latestReport = reports[0];
  const avgCompliance = latestReport?.summary?.overall_compliance_rate || 0;

  return (
    <div className={`min-h-screen p-6 ${isDarkMode ? 'bg-[#0a0a0f] text-white' : 'bg-slate-50 text-slate-900'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-2xl font-bold font-rajdhani ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Compliance Reporting
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
            Automated compliance reports, email digests, and trend analysis
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={sendDigest}
            disabled={sendingDigest}
            variant="outline"
            className={isDarkMode ? 'border-white/10 text-white hover:bg-white/10' : ''}
          >
            {sendingDigest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
            Send Digest
          </Button>
          <Button
            onClick={() => generateReport('summary')}
            disabled={generating}
            className="bg-indigo-600 hover:bg-indigo-500 text-white"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
            Generate Report
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getComplianceBg(avgCompliance)}`}>
              <Shield className={`w-5 h-5 ${getComplianceColor(avgCompliance)}`} />
            </div>
            <div>
              <p className={`text-2xl font-bold ${getComplianceColor(avgCompliance)}`}>{avgCompliance}%</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Overall Compliance</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-indigo-600/20' : 'bg-indigo-100'}`}>
              <FileText className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{reports.length}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Reports Generated</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-purple-600/20' : 'bg-purple-100'}`}>
              <Calendar className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{schedules.length}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Scheduled Reports</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-emerald-600/20' : 'bg-emerald-100'}`}>
              <TrendingUp className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>+5.2%</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>30-Day Trend</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className={`${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
          <TabsTrigger value="reports" className={`gap-2 ${isDarkMode ? 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white' : ''}`}>
            <FileText className="w-4 h-4" />
            Reports
          </TabsTrigger>
          <TabsTrigger value="trends" className={`gap-2 ${isDarkMode ? 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white' : ''}`}>
            <BarChart3 className="w-4 h-4" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="schedules" className={`gap-2 ${isDarkMode ? 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white' : ''}`}>
            <Calendar className="w-4 h-4" />
            Schedules
          </TabsTrigger>
        </TabsList>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Generated Reports
              </h3>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => generateReport('detailed')}
                  disabled={generating}
                  className={isDarkMode ? 'border-white/10' : ''}
                >
                  Detailed Report
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => generateReport('executive')}
                  disabled={generating}
                  className={isDarkMode ? 'border-white/10' : ''}
                >
                  Executive Summary
                </Button>
              </div>
            </div>
            
            <div className="space-y-3">
              {reports.length === 0 ? (
                <div className={`text-center py-12 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No reports generated yet</p>
                  <p className="text-sm mt-1">Click "Generate Report" to create your first compliance report</p>
                </div>
              ) : (
                reports.map((report) => (
                  <div
                    key={report.id}
                    onClick={() => { setSelectedReport(report); setShowReportDialog(true); }}
                    className={`p-4 rounded-lg cursor-pointer transition-all ${isDarkMode ? 'bg-zinc-800/50 hover:bg-zinc-800' : 'bg-slate-50 hover:bg-slate-100'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {report.type?.charAt(0).toUpperCase() + report.type?.slice(1)} Compliance Report
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            Generated {new Date(report.generated_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`text-lg font-bold ${getComplianceColor(report.summary?.overall_compliance_rate || 0)}`}>
                          {report.summary?.overall_compliance_rate || 0}%
                        </span>
                        <ChevronRight className={`w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Compliance Score Trends (30 Days)
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#e5e7eb'} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                    tickFormatter={(v) => v.split('-').slice(1).join('/')}
                  />
                  <YAxis 
                    tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                    domain={[0, 100]}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDarkMode ? '#18181b' : '#ffffff',
                      border: isDarkMode ? '1px solid #3f3f46' : '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="compliance" name="Overall" stroke="#6366f1" fill="#6366f1" fillOpacity={0.2} />
                  <Area type="monotone" dataKey="owasp" name="OWASP" stroke="#22c55e" fill="#22c55e" fillOpacity={0.1} />
                  <Area type="monotone" dataKey="pci" name="PCI-DSS" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} />
                  <Area type="monotone" dataKey="gdpr" name="GDPR" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.1} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Compliance by Standard
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={[
                        { name: 'OWASP', value: 85 },
                        { name: 'PCI-DSS', value: 72 },
                        { name: 'GDPR', value: 78 },
                        { name: 'SOC 2', value: 65 },
                        { name: 'NIST', value: 70 }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}%`}
                    >
                      {COLORS.map((color, index) => (
                        <Cell key={`cell-${index}`} fill={color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
            </div>

            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Key Metrics
              </h3>
              <div className="space-y-4">
                {[
                  { label: 'Total Checks Performed', value: '247', trend: '+12%', up: true },
                  { label: 'Critical Failures', value: '3', trend: '-2', up: false },
                  { label: 'Avg. Resolution Time', value: '4.2 days', trend: '-0.8d', up: false },
                  { label: 'Compliance Velocity', value: '2.1%/week', trend: '+0.3%', up: true }
                ].map((metric, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                      {metric.label}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {metric.value}
                      </span>
                      <span className={`text-xs flex items-center ${metric.up ? 'text-green-500' : 'text-red-500'}`}>
                        {metric.up ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        {metric.trend}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Schedules Tab */}
        <TabsContent value="schedules" className="space-y-4">
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Scheduled Reports
              </h3>
              <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-600 hover:bg-indigo-500 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    New Schedule
                  </Button>
                </DialogTrigger>
                <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                  <DialogHeader>
                    <DialogTitle>Create Report Schedule</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Schedule Name</Label>
                      <Input
                        value={newSchedule.name}
                        onChange={(e) => setNewSchedule({...newSchedule, name: e.target.value})}
                        placeholder="Weekly Compliance Summary"
                        className={isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}
                      />
                    </div>
                    <div>
                      <Label>Frequency</Label>
                      <Select value={newSchedule.frequency} onValueChange={(v) => setNewSchedule({...newSchedule, frequency: v})}>
                        <SelectTrigger className={isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Report Type</Label>
                      <Select value={newSchedule.report_type} onValueChange={(v) => setNewSchedule({...newSchedule, report_type: v})}>
                        <SelectTrigger className={isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="summary">Summary</SelectItem>
                          <SelectItem value="detailed">Detailed</SelectItem>
                          <SelectItem value="executive">Executive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Recipients (comma-separated)</Label>
                      <Input
                        value={newSchedule.recipients}
                        onChange={(e) => setNewSchedule({...newSchedule, recipients: e.target.value})}
                        placeholder="security@company.com, ciso@company.com"
                        className={isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}
                      />
                    </div>
                    <Button onClick={createSchedule} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white">
                      Create Schedule
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              {schedules.length === 0 ? (
                <div className={`text-center py-12 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No scheduled reports</p>
                  <p className="text-sm mt-1">Create a schedule to automate compliance reporting</p>
                </div>
              ) : (
                schedules.map((schedule) => (
                  <div
                    key={schedule.id}
                    className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Calendar className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {schedule.name}
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            {schedule.frequency} • {schedule.report_type} report
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs px-2 py-1 rounded ${schedule.enabled ? 'bg-green-500/20 text-green-500' : 'bg-zinc-500/20 text-zinc-500'}`}>
                          {schedule.enabled ? 'Active' : 'Paused'}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteSchedule(schedule.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Report Detail Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className={`max-w-3xl max-h-[90vh] overflow-auto ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              Compliance Report
            </DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-3">
                <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-2xl font-bold ${getComplianceColor(selectedReport.summary?.overall_compliance_rate || 0)}`}>
                    {selectedReport.summary?.overall_compliance_rate || 0}%
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Overall</p>
                </div>
                <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-2xl font-bold text-green-500`}>{selectedReport.summary?.passed || 0}</p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Passed</p>
                </div>
                <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-2xl font-bold text-red-500`}>{selectedReport.summary?.failed || 0}</p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Failed</p>
                </div>
                <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {selectedReport.summary?.total_checks || 0}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Total</p>
                </div>
              </div>

              {/* Standards Breakdown */}
              {selectedReport.standards_breakdown && selectedReport.standards_breakdown.length > 0 && (
                <div>
                  <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Standards Breakdown
                  </h4>
                  <div className="space-y-2">
                    {selectedReport.standards_breakdown.map((std, idx) => (
                      <div key={idx} className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                        <div className="flex items-center justify-between">
                          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {std.standard?.toUpperCase()}
                          </span>
                          <span className={`font-bold ${getComplianceColor(std.compliance_rate)}`}>
                            {std.compliance_rate}%
                          </span>
                        </div>
                        <div className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          {std.passed}/{std.total_checks} passed • Avg score: {std.average_score}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {selectedReport.recommendations && (
                <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-indigo-900/20 border-indigo-500/30' : 'bg-indigo-50 border-indigo-200'}`}>
                  <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                    Recommendations
                  </h4>
                  <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                    {selectedReport.recommendations.map((rec, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Generated: {new Date(selectedReport.generated_at).toLocaleString()} by {selectedReport.generated_by}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
